import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-243 — OpenRouter model autocomplete.
//
// Replaces the hardcoded POPULAR_MODELS dropdown + free-text override
// pattern in CreateAgentV2Dialog and ModelPicker with a single
// fuzzy-search combobox sourced from OpenRouter's `/api/v1/models`
// endpoint (no auth required for listing). Default selection is
// google/gemma-4-31b-it — same model Agatha runs, picked because it's
// the cheapest capable option, so an accidental "Create agent" without
// a deliberate model choice doesn't burn workspace spend.
//
// The endpoint returns ~300+ models. We fetch once per component mount
// (no shared cache yet — KTK-243 may grow one if it becomes a hotspot)
// and filter client-side. Prefix-on-id ranks above substring; pricing
// and context length are surfaced inline so the user can pick by cost
// signal at-a-glance.
import { useEffect, useMemo, useRef, useState } from "react";
const FETCH_URL = "https://openrouter.ai/api/v1/models";
let cachedModels = null;
let cacheInflight = null;
function fetchModels() {
    if (cachedModels)
        return Promise.resolve(cachedModels);
    if (cacheInflight)
        return cacheInflight;
    cacheInflight = fetch(FETCH_URL)
        .then((r) => r.json())
        .then((d) => {
        cachedModels = Array.isArray(d.data) ? d.data : [];
        return cachedModels;
    })
        .catch(() => {
        cacheInflight = null;
        throw new Error("Could not load OpenRouter model list");
    });
    return cacheInflight;
}
function rankMatches(models, query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return models.slice(0, 60);
    const matches = [];
    for (const m of models) {
        const id = m.id.toLowerCase();
        const name = (m.name ?? "").toLowerCase();
        if (id === q)
            matches.push({ m, score: 0 });
        else if (id.startsWith(q))
            matches.push({ m, score: 1 });
        else if (name.startsWith(q))
            matches.push({ m, score: 2 });
        else if (id.includes(q))
            matches.push({ m, score: 3 });
        else if (name.includes(q))
            matches.push({ m, score: 4 });
    }
    matches.sort((a, b) => a.score - b.score || a.m.id.localeCompare(b.m.id));
    return matches.slice(0, 60).map((x) => x.m);
}
function formatPrice(pricing) {
    if (!pricing)
        return null;
    const prompt = parseFloat(pricing.prompt ?? "");
    const completion = parseFloat(pricing.completion ?? "");
    if (!Number.isFinite(prompt) && !Number.isFinite(completion))
        return null;
    // OpenRouter prices are USD per token; multiply by 1M for "per million".
    const fmt = (v) => {
        if (!Number.isFinite(v))
            return "?";
        if (v === 0)
            return "free";
        const perM = v * 1_000_000;
        if (perM >= 1)
            return `$${perM.toFixed(2)}/M`;
        return `$${perM.toFixed(3)}/M`;
    };
    return `${fmt(prompt)} in / ${fmt(completion)} out`;
}
export function OpenRouterModelPicker({ value, onChange, placeholder = "Search OpenRouter models…", fullWidth = true, variant = "form", }) {
    const [models, setModels] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState(value);
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const wrapperRef = useRef(null);
    useEffect(() => {
        let cancelled = false;
        fetchModels()
            .then((list) => {
            if (cancelled)
                return;
            setModels(list);
        })
            .catch((e) => {
            if (cancelled)
                return;
            setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, []);
    // Sync query → value when value changes externally (e.g. parent
    // resetting the form). Don't sync the other way; query reflects
    // what the user is *typing*, which can differ from the committed
    // value mid-search.
    useEffect(() => {
        setQuery(value);
    }, [value]);
    // Click-outside-to-close. Mousedown handles the case where the user
    // clicks somewhere else immediately — pointerup-based detection
    // would close after the click on a list item, blocking selection.
    useEffect(() => {
        if (!open)
            return;
        function onDown(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);
    const ranked = useMemo(() => {
        if (!models)
            return [];
        return rankMatches(models, query);
    }, [models, query]);
    function commit(modelId) {
        onChange(modelId);
        setQuery(modelId);
        setOpen(false);
    }
    function onKeyDown(e) {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open)
                setOpen(true);
            setActiveIdx((i) => Math.min(i + 1, Math.max(0, ranked.length - 1)));
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        }
        else if (e.key === "Enter") {
            const pick = ranked[activeIdx];
            if (pick) {
                e.preventDefault();
                commit(pick.id);
            }
            else if (query.trim()) {
                // No match in the list but the user typed something — treat as
                // a paste-in fallback (OpenRouter accepts arbitrary ids; a new
                // model may not be in our cache yet).
                commit(query.trim());
            }
        }
        else if (e.key === "Escape") {
            setOpen(false);
            setQuery(value);
        }
    }
    const listMaxHeight = variant === "compact" ? 220 : 280;
    return (_jsxs("div", { ref: wrapperRef, style: { position: "relative", width: fullWidth ? "100%" : undefined }, children: [_jsx("input", { type: "text", value: query, onChange: (e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    setActiveIdx(0);
                }, onFocus: () => setOpen(true), onKeyDown: onKeyDown, placeholder: placeholder, autoComplete: "off", spellCheck: false, style: inputStyle(variant) }), open && (_jsxs("div", { role: "listbox", style: {
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    background: "var(--paper-2)",
                    border: "1px solid var(--hairline)",
                    borderRadius: "var(--radius-sm)",
                    boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
                    maxHeight: listMaxHeight,
                    overflow: "auto",
                    zIndex: 20,
                    fontSize: "var(--fs-meta)",
                }, children: [error && (_jsxs("div", { style: { padding: "8px 10px", color: "var(--accent-ink)" }, children: [error, ". You can still paste any OpenRouter model id."] })), !error && models === null && (_jsx("div", { style: { padding: "8px 10px", color: "var(--ink-4)" }, children: "Loading models\u2026" })), !error && models !== null && ranked.length === 0 && (_jsxs("div", { style: { padding: "8px 10px", color: "var(--ink-4)" }, children: ["No matches. Press Enter to use", " ", _jsx("span", { style: { fontFamily: "var(--mono)", color: "var(--ink-2)" }, children: query.trim() || value }), " ", "as-is."] })), ranked.map((m, i) => {
                        const price = formatPrice(m.pricing);
                        const active = i === activeIdx;
                        return (_jsxs("div", { role: "option", "aria-selected": active, onMouseEnter: () => setActiveIdx(i), onMouseDown: (e) => {
                                // mousedown not click — input loses focus on click and
                                // wrapper would close before handler fires otherwise.
                                e.preventDefault();
                                commit(m.id);
                            }, style: {
                                padding: "6px 10px",
                                cursor: "pointer",
                                background: active ? "var(--accent-soft)" : "transparent",
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                borderBottom: "1px solid var(--hairline)",
                            }, children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", gap: 8 }, children: [_jsx("span", { style: {
                                                fontFamily: "var(--mono)",
                                                fontSize: "var(--fs-meta)",
                                                color: "var(--ink)",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }, children: m.id }), price && (_jsx("span", { style: {
                                                fontFamily: "var(--mono)",
                                                fontSize: 11,
                                                color: "var(--ink-3)",
                                                whiteSpace: "nowrap",
                                            }, children: price }))] }), m.name && m.name !== m.id && (_jsxs("div", { style: {
                                        fontSize: 11,
                                        color: "var(--ink-3)",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }, children: [m.name, m.context_length ? ` · ${(m.context_length / 1000).toFixed(0)}K ctx` : ""] }))] }, m.id));
                    })] }))] }));
}
function inputStyle(variant) {
    return {
        width: "100%",
        padding: variant === "compact" ? "6px 10px" : "6px 10px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink)",
        font: "inherit",
        fontFamily: "var(--mono)",
        fontSize: "var(--fs-body)",
    };
}
//# sourceMappingURL=OpenRouterModelPicker.js.map